// blockPlayer.js - полностью переработанная версия
import * as Tone from 'tone';
import { getCurrentTempo, getMeasuresForBlock } from './notes.js';
import { threeJSBand } from "./threejsBand";
import { getGamingMode, stopGame } from "./game";
import { stopCountdown } from "./utils/countdown.js";
import { stopAudio } from "./tuner.js";
import { audioNormalizer } from "./audioNormalizer.js";
import { guitarEffects } from "./guitarEffects.js";

class BlockPlayer {
    constructor() {
        // Глобальные переменные
        this.isPlaying = false;
        this.currentBlockId = null;
        this.playbackResolve = null;
        this.playbackReject = null;
        this.scheduledTimeouts = [];
        this.tempo = 120;
        this.samplerConnectedToEffects = false; // Флаг подключения семплера к эффектам

        this.sampler = this.createSampler();

    }

    createSampler() {
        const noteMap = {
                E2: "normTNHigh40.mp3",
                F2: "normTNHigh41.mp3",
                "F#2": "normTNHigh42.mp3",
                G2: "normTNHigh43.mp3",
                "G#2": "normTNHigh44.mp3",
                A2: "normTNHigh45.mp3",
                "A#2": "normTNHigh46.mp3",
                B2: "normTNHigh47.mp3",
                C3: "normTNHigh48.mp3",
                "C#3": "normTNHigh49.mp3",
                D3: "normTNHigh50.mp3",
                "D#3": "normTNHigh51.mp3",
                E3: "normTNHigh52.mp3",
                F3: "normTNHigh53.mp3",
                "F#3": "normTNHigh54.mp3",
                G3: "normTNHigh55.mp3",
                "G#3": "normTNHigh56.mp3",
                A3: "normTNHigh57.mp3",
                "A#3": "normTNHigh58.mp3",
                B3: "normTNHigh59.mp3",
                C4: "normTNHigh60.mp3",
                "C#4": "normTNHigh61.mp3",
                D4: "normTNHigh62.mp3",
                "D#4": "normTNHigh63.mp3",
                E4: "normTNHigh64.mp3",
                F4: "normTNHigh65.mp3",
                "F#4": "normTNHigh66.mp3",
                G4: "normTNHigh67.mp3",
                "G#4": "normTNHigh68.mp3",
                A4: "normTNHigh69.mp3",
                "A#4": "normTNHigh70.mp3",
                B4: "normTNHigh71.mp3",
                C5: "normTNHigh72.mp3",
                "C#5": "normTNHigh73.mp3",
                D5: "normTNHigh74.mp3",
                "D#5": "normTNHigh75.mp3",
                E5: "normTNHigh76.mp3",
                F5: "normTNHigh77.mp3",
                "F#5": "normTNHigh78.mp3",
                G5: "normTNHigh79.mp3",
                "G#5": "normTNHigh80.mp3",
                A5: "normTNHigh81.mp3",
                "A#5": "normTNHigh82.mp3",
                B5: "normTNHigh83.mp3",
                C6: "normTNHigh84.mp3"
        };

        const sampler = new Tone.Sampler({
            urls: noteMap,
            release: 1,
            baseUrl: "/audio/samples/guitar/",
            volume: -12, // Устанавливаем базовый уровень
            onerror: (err) => console.error("Ошибка загрузки семплера", err)
        });
        
        // Нормализуем семплер
        audioNormalizer.initialize();
        audioNormalizer.normalizeSampler(sampler);
        // По умолчанию подключаем к нормализатору
        sampler.connect(audioNormalizer.getInput());
        
        return sampler;
    }

    // Переключение подключения семплера между нормализатором и destination
    async switchSamplerConnection(useEffects = false) {
        if (!this.sampler) return;

        try {
            // Отключаем семплер от текущего подключения
            this.sampler.disconnect();
            this.samplerConnectedToEffects = false;

            if (useEffects) {
                // Для режима прослушивания подключаем напрямую к Tone.Destination
                // Это позволяет использовать встроенные эффекты Tone.js
                // и избежать проблем с разными AudioContext
                this.sampler.toDestination();
                this.samplerConnectedToEffects = true;
            } else {
                // Для игрового режима подключаем к нормализатору
                this.sampler.connect(audioNormalizer.getInput());
            }
        } catch (error) {
            console.error('Error switching sampler connection:', error);
            // Fallback: подключаем к нормализатору
            try {
                this.sampler.disconnect();
                this.sampler.connect(audioNormalizer.getInput());
                this.samplerConnectedToEffects = false;
            } catch (fallbackError) {
                // Игнорируем ошибки fallback
            }
        }
    }

    // Воспроизведение блока с использованием setTimeout вместо Tone.Part
    async playBlock(blockId, scheduledStartTime = null) {
        return new Promise(async (resolve, reject) => {
            // КРИТИЧНО: Проверяем, не была ли остановлена последовательность ДО запуска блока
            const { sequencePlayer } = await import('./sequencePlayer.js');
            if (sequencePlayer && sequencePlayer.shouldStop) {
                console.log('⛔ playBlock прерван: sequencePlayer остановлен');
                reject(new Error('Playback stopped manually'));
                return;
            }
            
            if (this.isPlaying) {
                await this.stopBlockPlayback(false);
            }

            // ВАЖНО: Сбрасываем флаг isPlayAllMode только если блок НЕ вызывается из sequencePlayer
            // Если scheduledStartTime установлен, это означает, что блок вызывается из sequencePlayer
            // и мы находимся в режиме playAll - не сбрасываем флаг в этом случае
            // Флаг будет сброшен после завершения всей последовательности
            // Также сбрасываем isListenMode для обычного запуска
            if (scheduledStartTime === null) {
                // Обычный запуск блока (не из sequencePlayer) - сбрасываем флаги
                window.isPlayAllMode = false;
                // НЕ сбрасываем isListenMode здесь, так как он управляется из app.js
                // Вместо этого переключаем подключение семплера в зависимости от режима
                const isListenMode = window.isListenMode === true;
                await this.switchSamplerConnection(isListenMode);
            }
            
            // КРИТИЧЕСКИ ВАЖНО: Очищаем все запланированные таймауты перед новым запуском
            // Это предотвращает воспроизведение старых звуков с задержкой
            this.scheduledTimeouts.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            this.scheduledTimeouts = [];
            
            // Также останавливаем все звуки семплера перед новым запуском
            this.sampler.releaseAll();

            this.playbackResolve = resolve;
            this.playbackReject = reject;
            this.currentBlockId = blockId;
            const blockMeasures = getMeasuresForBlock(blockId);

            if (!blockMeasures || blockMeasures.length === 0) {
                const error = new Error('Блок не найден или пуст');
                reject(error);
                return;
            }

            try {
                // КРИТИЧЕСКИ ВАЖНО: Для обычного запуска убеждаемся, что Tone.js контекст активен
                // Это особенно важно после playAll, когда контекст может быть приостановлен
                if (scheduledStartTime === null) {
                    const toneContext = Tone.getContext();
                    if (toneContext.state !== 'running') {
                        await Tone.start();
                    }
                }
                
                this.tempo = getCurrentTempo() || 120;
                this.isPlaying = true;
                
                // Очищаем ноты в начале воспроизведения (если не игровой режим)
                if (!getGamingMode() && threeJSBand) {
                    threeJSBand.clearAllMovingNotes();
                }
                
                const events = [];
                let timeCursor = 0;
                let totalDuration = 0;

                // Собираем все события
                blockMeasures.forEach(measure => {
                    measure.forEach(chordGroup => {
                        const isPause = chordGroup[0] && chordGroup[0].pause === "true";

                        if (isPause) {
                            events.push({
                                time: timeCursor,
                                isPause: true,
                                duration: this.calculateDuration({duration: chordGroup[0].duration, dotted: chordGroup[0].dotted}),
                                durationMs: this.calculateDurationMs({duration: chordGroup[0].duration, dotted: chordGroup[0].dotted})
                            });

                            const stepDuration = 4 / chordGroup[0].duration;
                            const actualDuration = chordGroup[0].dotted ? stepDuration * 1.5 : stepDuration;
                            timeCursor += actualDuration;
                            totalDuration += actualDuration;
                        } else {
                            const notes = chordGroup.map(note => note.noteName);
                            const durationValue = chordGroup[0].duration;
                            const isDotted = chordGroup[0].dotted;

                            if (notes.length > 0) {
                                events.push({
                                    time: timeCursor,
                                    notes: notes,
                                    duration: this.calculateDuration(chordGroup[0]),
                                    animationData: {
                                        notes: chordGroup,
                                        duration: this.calculateDurationMs(chordGroup[0]),
                                        absoluteTime: 0
                                    },
                                    isPause: false
                                });
                            }

                            const stepDuration = 4 / durationValue;
                            const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;
                            timeCursor += actualDuration;
                            totalDuration += actualDuration;
                        }
                    });
                });

                // Для обычного запуска (не из sequencePlayer) используем текущее время
                // Для вызова из sequencePlayer используем переданное scheduledStartTime
                // ВАЖНО: Для обычного запуска планируем звуки относительно текущего времени,
                // чтобы избежать задержек после playAll
                const isFromSequencePlayer = scheduledStartTime !== null;
                
                // Переключаем подключение семплера в зависимости от режима прослушивания
                // Это нужно делать и для sequencePlayer, если мы в режиме прослушивания
                const isListenMode = window.isListenMode === true;
                if (isListenMode) {
                    await this.switchSamplerConnection(true);
                } else if (!isFromSequencePlayer) {
                    // Для обычного запуска (не из sequencePlayer) переключаем на нормализатор
                    await this.switchSamplerConnection(false);
                }
                
                // Для обычного запуска НЕ используем baseStartTime и eventStartTime,
                // так как они рассчитываются в начале и могут быть в прошлом к моменту выполнения setTimeout
                // Вместо этого рассчитываем все относительно момента выполнения setTimeout
                let baseStartTime, startTime;
                if (isFromSequencePlayer) {
                    baseStartTime = scheduledStartTime;
                    startTime = baseStartTime + 0.1; // Небольшая задержка для стабильности
                } else {
                // Для обычного запуска не используем эти значения для звука
                // Они нужны только для анимации нот
                baseStartTime = Tone.now();
                startTime = baseStartTime + 0.1;
            }

            // Минимальное время полета для каждой ноты (в секундах)
            const MIN_NOTE_TRAVEL_TIME = 1.5;
            
            // Сбрасываем счетчик для новых нот
            window._noteDebugCounter = 0;
            
            // Планируем события
            events.forEach((event, eventIndex) => {
                // Проверяем на остановку перед планированием каждого события
                if (sequencePlayer && sequencePlayer.shouldStop) {
                    console.log('⛔ Прерывание планирования событий: sequencePlayer остановлен');
                    return;
                }
                // Рассчитываем абсолютное время достижения грифа для этого события
                const relativeEventTime = this.calculateDelaySeconds(event.time, this.tempo);
                const eventStartTime = startTime + relativeEventTime;

                    if (event.isPause) {
                        // Для паузы просто создаем таймаут
                        const timeoutId = setTimeout(() => {
                            if (!this.isPlaying) return;
                        }, this.calculateDelayMs(event.time, this.tempo));
                        this.scheduledTimeouts.push(timeoutId);
                    } else {
                        // Для нот/аккордов - создаем движущиеся ноты
                        const notesArray = event.animationData.notes;
                        // Первое событие - первая группа нот
                        const isFirstGroup = eventIndex === 0;
                        
                        // КАЖДАЯ группа нот имеет свой startTime, рассчитанный из targetTime
                        // Это обеспечивает одинаковую скорость для всех нот
                        const noteStartTime = eventStartTime - MIN_NOTE_TRAVEL_TIME;
                        
                        notesArray.forEach((noteData, noteIndex) => {
                            if (threeJSBand) {
                                // Ищем следующую ноту на той же струне для эффекта hammer
                                let nextNoteOnString = null;
                                if (noteData.effect && noteData.effect.hammer) {
                                    for (let i = noteIndex + 1; i < notesArray.length; i++) {
                                        if (notesArray[i].string === noteData.string) {
                                            nextNoteOnString = notesArray[i];
                                            break;
                                        }
                                    }
                                }
                                
                                // Каждая группа нот имеет свой startTime = targetTime - MIN_NOTE_TRAVEL_TIME
                                // Это гарантирует одинаковую скорость и правильное расположение по оси Z
                                threeJSBand.addMovingNote(
                                    noteData,
                                    noteStartTime, // Время создания = targetTime - MIN_NOTE_TRAVEL_TIME
                                    eventStartTime, // Момент достижения грифа
                                    true,
                                    nextNoteOnString,
                                    isFirstGroup // Флаг первой группы
                                );
                            }
                        });

                        // Планируем воспроизведение звука
                        // ВАЖНО: Проверяем флаги в момент планирования, а не в момент выполнения
                        // Это гарантирует, что звук будет воспроизведен, даже если флаг изменится после планирования
                        
                        // КРИТИЧЕСКИ ВАЖНО: Если это обычный запуск (не из sequencePlayer),
                        // звуки ДОЛЖНЫ играть, независимо от флага isPlayAllMode
                        // (флаг уже сброшен выше, но на всякий случай проверяем scheduledStartTime)
                        // В режиме прослушивания звук тоже должен играть, но через эффекты
                        const shouldPlaySound = !isFromSequencePlayer;
                        const shouldMarkNotes = !getGamingMode() && shouldPlaySound && !(window.isListenMode === true);
                        
                        // Вычисляем задержку для setTimeout
                        // КРИТИЧЕСКИ ВАЖНО: Для обычного запуска используем простое относительное время
                        // от момента создания setTimeout, чтобы избежать проблем с синхронизацией после playAll
                        let delayMs;
                        
                        if (isFromSequencePlayer) {
                            // Для sequencePlayer используем точное запланированное время
                            const currentTimeAtPlanning = Tone.now();
                            delayMs = Math.max(0, (eventStartTime - currentTimeAtPlanning) * 1000);
                        } else {
                            // Для обычного запуска используем простое относительное время от текущего момента
                            // КРИТИЧЕСКИ ВАЖНО: Используем performance.now() для расчета delayMs,
                            // чтобы избежать проблем с синхронизацией Tone.now() после playAll
                            const relativeEventTime = this.calculateDelaySeconds(event.time, this.tempo);
                            // Для первого события используем минимальную задержку (50мс), для остальных - относительно начала
                            const baseDelay = eventIndex === 0 ? 0.05 : 0;
                            
                            // КРИТИЧЕСКИ ВАЖНО: Используем performance.now() для расчета delayMs,
                            // так как это более надежно после playAll, когда Tone.now() может быть несинхронизирован
                            const planningTimeMs = performance.now();
                            delayMs = Math.max(0, (baseDelay + relativeEventTime) * 1000);
                            
                            const soundTimeoutId = setTimeout(async () => {
                                // Проверяем остановку перед воспроизведением звука
                                if (!this.isPlaying || (sequencePlayer && sequencePlayer.shouldStop)) {
                                    console.log('⛔ Пропуск воспроизведения звука: остановлено');
                                    return;
                                }

                                if (shouldPlaySound) {
                                    // КРИТИЧЕСКИ ВАЖНО: Убеждаемся, что Tone.js контекст активен перед воспроизведением
                                    const toneContext = Tone.getContext();
                                    if (toneContext.state !== 'running') {
                                        await Tone.start();
                                    }
                                    
                                    // КРИТИЧЕСКАЯ ПРОБЛЕМА: После playAll Tone.now() возвращает огромные значения (59-60 сек)
                                    // Это ломает воспроизведение звука
                                    // РЕШЕНИЕ: Если Tone.now() слишком большой, не передаем время вообще
                                    const toneNow = Tone.now();
                                    
                                    try {
                                        // Если Tone.now() слишком большой (после playAll), не передаем время
                                        // Tone.js использует текущее время автоматически
                                        if (toneNow > 10) {
                                            this.sampler.triggerAttackRelease(event.notes, event.duration);
                                        } else {
                                            this.sampler.triggerAttackRelease(event.notes, event.duration, toneNow);
                                        }
                                    } catch (triggerError) {
                                        console.error('Ошибка воспроизведения звука:', triggerError.message);
                                    }
                                }

                                // В обычном режиме отмечаем ноты как сыгранные
                                if (shouldMarkNotes) {
                                    event.animationData.notes.forEach(noteData => {
                                        if (threeJSBand) {
                                            threeJSBand.markNoteAsHit(noteData.string, noteData.fret);
                                        }
                                    });
                                }
                            }, delayMs);
                            
                            this.scheduledTimeouts.push(soundTimeoutId);
                            return; // Пропускаем код ниже для обычного запуска
                        }
                        
                        // Код для sequencePlayer (isFromSequencePlayer === true)
                        const soundTimeoutId = setTimeout(async () => {
                            // Проверяем остановку перед воспроизведением звука
                            if (!this.isPlaying || (sequencePlayer && sequencePlayer.shouldStop)) {
                                console.log('⛔ Пропуск воспроизведения звука: остановлено');
                                return;
                            }

                            if (shouldPlaySound) {
                                // Для sequencePlayer используем запланированное время
                                const playTime = Math.max(Tone.now(), eventStartTime);
                                this.sampler.triggerAttackRelease(event.notes, event.duration, playTime);
                            }

                            // В обычном режиме отмечаем ноты как сыгранные
                            if (shouldMarkNotes) {
                                event.animationData.notes.forEach(noteData => {
                                    if (threeJSBand) {
                                        threeJSBand.markNoteAsHit(noteData.string, noteData.fret);
                                    }
                                });
                            }
                            // НО НЕ в режимах "проиграть все" и "прослушать песню"
                            if (shouldMarkNotes) {
                                event.animationData.notes.forEach(noteData => {
                                    if (threeJSBand) {
                                        threeJSBand.markNoteAsHit(noteData.string, noteData.fret);
                                    }
                                });
                            }

                        }, delayMs);

                        this.scheduledTimeouts.push(soundTimeoutId);
                    }
                });

                // После добавления всех нот, обновляем hammer эффекты
                // чтобы найти следующие ноты на тех же струнах
                if (threeJSBand) {
                    threeJSBand.updateHammerEffects();
                }

                // Планируем завершение воспроизведения
                const totalDelayMs = this.calculateDelayMs(totalDuration, this.tempo);
                
                // Планируем очистку нот после завершения воспроизведения (если не игровой режим)
                // Добавляем задержку чтобы ноты успели отобразиться после завершения звучания
                // Очистка происходит после того, как все ноты отыграют свое время звучания
                const maxNoteDuration = Math.max(...events
                    .filter(e => !e.isPause && e.animationData)
                    .map(e => e.animationData.duration || 0), 0);
                const cleanupDelayMs = totalDelayMs + maxNoteDuration + 500; // +500мс запас
                
                const cleanupTimeoutId = setTimeout(() => {
                    if (!getGamingMode() && threeJSBand && this.isPlaying) {
                        // Очищаем только ноты, которые уже отыграли свое время (проверяем по duration)
                        threeJSBand.cleanupNotesAfterPlayback();
                    }
                }, cleanupDelayMs);
                this.scheduledTimeouts.push(cleanupTimeoutId);
                
                const completionTimeoutId = setTimeout(() => {
                    if (this.isPlaying) {
                        this.isPlaying = false;
                        
                        if (this.playbackResolve) {
                            this.playbackResolve();
                        }
                    }
                }, totalDelayMs);

                this.scheduledTimeouts.push(completionTimeoutId);
                this.highlightCurrentBlock(blockId, true);

            } catch (error) {
                console.error('Ошибка воспроизведения блока:', error);
                reject(error);
            }
        });
    }

    calculateDelaySeconds(timeInQuarters, tempo) {
        const quarterDurationSeconds = 60 / tempo;
        return timeInQuarters * quarterDurationSeconds;
    }

    // Расчет задержки в миллисекундах
    calculateDelayMs(timeInQuarters, tempo) {
        const quarterDurationMs = 60000 / tempo;
        return timeInQuarters * quarterDurationMs;
    }

    async stopBlockPlayback(shouldReject = true) {
        return new Promise(async (resolve) => {
            try {
                // Сбрасываем флаг isPlayAllMode при остановке блока
                // Это важно, чтобы при следующем запуске блока синтезатор работал
                window.isPlayAllMode = false;

                // Возвращаем семплер к нормальному подключению (к нормализатору)
                if (this.samplerConnectedToEffects) {
                    await this.switchSamplerConnection(false);
                }

                // Полная остановка игры и всех активностей, связанных с блоком
                try {
                    stopCountdown();
                } catch (e) {
                    console.warn('Не удалось остановить обратный отсчет:', e);
                }

                try {
                    stopGame();
                } catch (e) {
                    console.warn('Не удалось остановить игровой режим:', e);
                }

                try {
                    stopAudio();
                } catch (e) {
                    console.warn('Не удалось остановить тюнер/микрофон:', e);
                }

                // Очищаем все запланированные таймауты
                this.scheduledTimeouts.forEach(timeoutId => {
                    clearTimeout(timeoutId);
                });
                this.scheduledTimeouts = [];

                // Останавливаем все звуки семплера
                this.sampler.releaseAll();

                // ОЧИЩАЕМ НОТЫ ПРИ ПРИНУДИТЕЛЬНОЙ ОСТАНОВКЕ
                if (threeJSBand) {
                    threeJSBand.clearAllMovingNotes();
                }

                // Снимаем подсветку
                if (this.currentBlockId !== null) {
                    this.highlightCurrentBlock(this.currentBlockId, false);
                }

                // Разрешаем или отклоняем промис
                if (this.playbackResolve && this.playbackReject) {
                    if (shouldReject) {
                        this.playbackReject(new Error('Playback stopped manually'));
                    } else {
                        this.playbackResolve();
                    }
                }

                // Сбрасываем состояние
                this.isPlaying = false;
                this.currentBlockId = null;
                this.playbackResolve = null;
                this.playbackReject = null;

                resolve();

            } catch (error) {
                console.error('Ошибка остановки:', error);
                if (this.playbackReject && shouldReject) {
                    this.playbackReject(error);
                }
                resolve();
            }
        });
    }

    // Расчет длительности ноты в формате Tone.js
    calculateDuration(noteData) {
        let duration = "4n";

        switch(noteData.duration) {
            case 1: duration = "1n"; break;
            case 2: duration = "2n"; break;
            case 4: duration = "4n"; break;
            case 8: duration = "8n"; break;
            case 16: duration = "16n"; break;
            case 32: duration = "32n"; break;
            default: duration = "4n";
        }

        if (noteData.dotted) {
            duration += ".";
        }

        return duration;
    }

    // Расчет длительности в миллисекундах для анимации
    calculateDurationMs(noteData) {
        const quarterNoteDuration = 60000 / this.tempo;
        let durationMs;

        switch(noteData.duration) {
            case 1: durationMs = quarterNoteDuration * 4; break;
            case 2: durationMs = quarterNoteDuration * 2; break;
            case 4: durationMs = quarterNoteDuration; break;
            case 8: durationMs = quarterNoteDuration / 2; break;
            case 16: durationMs = quarterNoteDuration / 4; break;
            case 32: durationMs = quarterNoteDuration / 8; break;
            default: durationMs = quarterNoteDuration;
        }

        if (noteData.dotted) {
            durationMs *= 1.5;
        }

        return durationMs;
    }

    // Подсветка текущего блока
    highlightCurrentBlock(blockId, isPlaying) {
        const blockElement = document.querySelector(`.melody-block[data-block-id="${blockId}"]`);
        if (blockElement) {
            if (isPlaying) {
                blockElement.classList.add('playing');
            } else {
                blockElement.classList.remove('playing');
            }
        }
    }
}

const blockPlayer = new BlockPlayer();

/** Экземпляр плеера блоков для совместного использования (семплер и планирование) */
export const blockPlayerInstance = blockPlayer;

/**
 * Проигрывает музыкальный блок с визуализацией нот
 * @param {number} blockId
 * @param {number|null} scheduledStartTime - абсолютное время Tone.now() для старта (сек)
 */
export const playBlock = (blockId, scheduledStartTime = null) =>
    blockPlayer.playBlock(blockId, scheduledStartTime);

/** Принудительно останавливает воспроизведение текущего блока */
export const stopBlockPlayback = (shouldReject = true) =>
    blockPlayer.stopBlockPlayback(shouldReject);

/**
 * Рассчитывает длительность ноты в миллисекундах с учетом темпа/точки
 */
export const calculateDurationMs = (noteData) =>
    blockPlayer.calculateDurationMs(noteData);

/** Возвращает длительность в формате Tone.js (например, "4n.") */
export const calculateDuration = (noteData) =>
    blockPlayer.calculateDuration(noteData);










